import moment from 'moment'
import { MONTHLY_YEARLY_UPGRADE_PLAN_MAP } from '@/constant/monthlyToYearlyUpgrade'
import Util from '@/util'
import { formatCustomerFacedAmount } from '@/utils/pricing.utils'
import { isReportLockPreviewActive } from '@/business-logic/reportLockPreview'

const NEW_PRO_PLAN_CONFIG = { DATE: '2025-08-27', LIMIT: 2500 }

// AHA-42358 — the live entitlement (state.planBenefit, served by
// /api/pricing/entitlements) is the source of truth for the participant cap, so
// an A/B'd free limit (e.g. 100) surfaces instead of the hardcoded src/pricing.js
// value. maxAudience is a finite number for capped tiers and the string
// 'unlimited' for paid tiers; only a finite number is a usable display cap.
// Anything else (missing / 'unlimited' / non-numeric) falls back to the legacy
// static plan.limit logic, so paid display and the legacy path are unchanged.
const entitlementMaxAudience = (state) => {
  const maxAudience = state.planBenefit?.maxAudience
  return Number.isFinite(maxAudience) ? maxAudience : null
}

const monthlyParticipantLimit = (state) => {
  const limit = state.planBenefit?.maxParticipantMonthly
  return Number.isFinite(limit) ? limit : null
}

const legacyPlanLimit = (state) => {
  const subscription = state.currentUser?.myPlan?.subscription
  if (!subscription) return state.plan?.limit
  if (state.plan?.group === 'pro' && moment(subscription.current_period_start).isAfter(moment(NEW_PRO_PLAN_CONFIG.DATE))) {
    return NEW_PRO_PLAN_CONFIG.LIMIT
  }
  return state.plan?.limit
}

const planLimit = (state) => {
  const entitlementLimit = entitlementMaxAudience(state)
  if (entitlementLimit !== null) return entitlementLimit
  return legacyPlanLimit(state)
}

const eligibleToUpgradeFromMonthlyToYearly = (state) => {
  if (!state.monthlyToYearlyUpgradePreview ||
    !state.monthlyToYearlyUpgradePreview.amountToUpgrade ||
    state.monthlyToYearlyUpgradePreview.amountToUpgrade.amount === null ||
    state.monthlyToYearlyUpgradePreview.amountToUpgrade.amount === undefined
  ) return false
  return !!MONTHLY_YEARLY_UPGRADE_PLAN_MAP[state.currentUser.plan]
}

const monthlyToYearlyUpgradeAmount = (state) => {
  if (!state?.monthlyToYearlyUpgradePreview?.amountToUpgrade) return null
  const { amount, currencySymbol, currency } = state.monthlyToYearlyUpgradePreview.amountToUpgrade
  const monthlyAmount = Math.ceil(amount / 12)
  const customerFacedAmount = formatCustomerFacedAmount(monthlyAmount, currency)
  const formattedAmount = Util.formatNumberWithCommas(customerFacedAmount)
  return `${currencySymbol}${formattedAmount}`
}

const monthlyToYearlyUpgradePromotionCode = (state) => {
  return state.monthlyToYearlyUpgradePreview?.promotionCode
}

// --- Monthly-cancel retention offer (deep-discounted monthly→yearly switch) ---
// Reads from the isolated `yearlyRetentionOfferPreview` slot so it never collides
// with the upsell preview. All amounts come from the backend preview (context:
// 'cancel-retention'), which already applied a fixed 60% off the yearly list price
// (coupon GOYEARLY60).

const formatRetentionAmount = (amountObj) => {
  if (!amountObj || amountObj.amount === null || amountObj.amount === undefined) return null
  const { amount, currencySymbol, currency } = amountObj
  const customerFacedAmount = formatCustomerFacedAmount(amount, currency)
  const formattedAmount = Util.formatNumberWithCommas(customerFacedAmount)
  return `${currencySymbol}${formattedAmount}`
}

const eligibleForYearlyRetentionOffer = (state) => {
  const preview = state.yearlyRetentionOfferPreview
  if (!preview ||
    !preview.amountToUpgrade ||
    preview.amountToUpgrade.amount === null ||
    preview.amountToUpgrade.amount === undefined
  ) return false
  return !!MONTHLY_YEARLY_UPGRADE_PLAN_MAP[state.currentUser.plan]
}

// Per-month equivalent of the discounted yearly price (e.g. "$2.50"), for the headline.
const yearlyRetentionOfferMonthlyAmount = (state) => {
  const amountToUpgrade = state.yearlyRetentionOfferPreview?.amountToUpgrade
  if (!amountToUpgrade || amountToUpgrade.amount === null || amountToUpgrade.amount === undefined) return null
  const { amount, currencySymbol, currency } = amountToUpgrade
  const monthlyAmount = Math.ceil(amount / 12)
  const customerFacedAmount = formatCustomerFacedAmount(monthlyAmount, currency)
  const formattedAmount = Util.formatNumberWithCommas(customerFacedAmount)
  return `${currencySymbol}${formattedAmount}`
}

// Discounted yearly total actually charged.
const yearlyRetentionOfferTotalAmount = (state) => {
  return formatRetentionAmount(state.yearlyRetentionOfferPreview?.amountToUpgrade)
}

// Undiscounted yearly total, shown struck-through for the anchor. Optional in the payload.
const yearlyRetentionOfferOriginalAmount = (state) => {
  return formatRetentionAmount(state.yearlyRetentionOfferPreview?.originalAmount)
}

const yearlyRetentionOfferDiscountPercentage = (state) => {
  const percentage = state.yearlyRetentionOfferPreview?.discountPercentage
  return Number.isFinite(percentage) ? percentage : null
}

const yearlyRetentionOfferPromotionCode = (state) => {
  return state.yearlyRetentionOfferPreview?.promotionCode
}

const shouldShowMonthlyToYearlyPromo = (state, getters, rootState, rootGetters) => {
  // Check if user has the feature enabled
  const hasFeatureEnabled = rootGetters['featureToggles/canUserOfferingMonthlyToYearly']

  // Check if user is eligible for upgrade
  const isEligible = getters.eligibleToUpgradeFromMonthlyToYearly

  const hasHadFirstEvent = !!state.currentUser?.firstEventStartedAt || !!state.userHasHostedEvents

  return hasFeatureEnabled &&
    isEligible &&
    hasHadFirstEvent
}

const canViewReport = (state) => (
  !isReportLockPreviewActive() && state.planBenefit?.reportAndAnalytics?.viewReport !== false
)

const viewReportRequiredPlan = (state) => state.planBenefit?.reportAndAnalytics?.viewReport_planName || 'pro'

export default {
  canViewReport,
  viewReportRequiredPlan,
  planLimit,
  eligibleToUpgradeFromMonthlyToYearly,
  monthlyToYearlyUpgradeAmount,
  monthlyToYearlyUpgradePromotionCode,
  shouldShowMonthlyToYearlyPromo,
  eligibleForYearlyRetentionOffer,
  yearlyRetentionOfferMonthlyAmount,
  yearlyRetentionOfferTotalAmount,
  yearlyRetentionOfferOriginalAmount,
  yearlyRetentionOfferDiscountPercentage,
  yearlyRetentionOfferPromotionCode,
  monthlyParticipantLimit,
}
